
import { Component, ViewEncapsulation, OnInit, OnDestroy, ViewChildren, AfterViewInit, QueryList, ElementRef, CUSTOM_ELEMENTS_SCHEMA, inject } from "@angular/core";
import { Router, NavigationStart, RouterOutlet, RouterModule } from '@angular/router';

import { Toast, ToastrModule, ToastrService } from 'ngx-toastr';

import { AlertService, AlertDialog, DialogType, AlertMessage, MessageSeverity } from '../services/alert.service';
import { NotificationService } from "../services/notification.service";
import { AccountService } from '../services/account.service';
import { LocalStoreManager } from '../services/local-store-manager.service';
import { AuthService } from '../services/auth.service';
import { ConfigurationService } from '../services/configuration.service';
import { Permission } from '../models/permission.model';

import { fadeAnimation } from '../services/animations';
import { Roles } from "../models/enums";
import { RolesNew } from "../models/enums";
import { Role } from "../models/role.model";
import { PACTHubService } from "../services/pacthub.service";

import { ViewChild } from "@angular/core";
import { CommonModule } from "@angular/common";
import { MainMenuComponent } from "./menus/main-menu/main-menu.component";
import { MatButtonModule } from "@angular/material/button";
declare let alertify: any;
import { FormsModule } from '@angular/forms';
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { IdleDialogComponent } from "./dialogs/idle-dialog/idle-dialog.component";
import { EMPTY, Observable } from 'rxjs';
import { retry, finalize, catchError, timeout } from 'rxjs/operators';
import { User } from '../models/user.model';
import { MatIconModule } from "@angular/material/icon";
import { ODPProjectWorkflowViewModel } from "../models/odpprojectworkflow/odpProjectWorkflow.model";




@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'],
  animations: [fadeAnimation],
  imports: [
    CommonModule,
    MainMenuComponent,
    MatButtonModule,
    MatIconModule,
    RouterOutlet,
    ToastrModule,
    FormsModule
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})

export class AppComponent implements AfterViewInit {
  appTitle = 'PACT 3.2';
  activeProjectTitle: null | string = null;
  allProjects: null | ODPProjectWorkflowViewModel[] = null;
  projectColorOptions: string[] = ['#b6ebfe', '#a2f2a2', '#e9e900', '#ebdcff', '#ffdda2']; // colors for project names (508 compliant):  blue, green, yellow, purple, orange 
  currentColor: string | null = null;
  isAppLoaded: boolean;
  isUserLoggedIn: boolean;
  hasSelectedRole: boolean;
  hasSelectedProject: boolean;
  removePrebootScreen: boolean;
  newNotificationCount = 0;
  stickyToasties: number[] = [];
  dataLoadingConsecutiveFailures = 0;
  notificationsLoadingSubscription: any;
  openedSideBar: boolean = false;
  warningIsBeingShown: boolean = false;

  private loggedInFullName: string = "";

  @ViewChild(RouterOutlet) outlet!: RouterOutlet;
  activatedRoute: any = '';
  toggleSideBar(manual: any = false) {
    this.openedSideBar = manual ? false : !this.openedSideBar;    
  }
  clickHome() {
    this.router.navigateByUrl('/home/actions');
  }


  constructor(
    storageManager: LocalStoreManager,
    private accountService: AccountService, 
    private alertService: AlertService, 
    private notificationService: NotificationService,
    private authService: AuthService, 
    public configurations: ConfigurationService, 
    private router: Router, 
    private hubService: PACTHubService,
    private dialog: MatDialog,
    private toastrService: ToastrService
  ) {

    storageManager.initialiseStorageSyncListener();

    // Entirety of custom config is moved to app.config.ts provideToastr
  }
  ngAfterViewInit(): void {
    if (this.outlet?.isActivated) {
      this.activatedRoute = this.outlet.activatedRoute;
    }
  }

  ngOnInit() {
    this.isUserLoggedIn = this.authService.isLoggedIn;
    this.hasSelectedRole = this.isUserLoggedIn && this.accountService.GetActiveRole() != null;
    this.hasSelectedProject = this.isUserLoggedIn && this.accountService.GetActiveProject() != null;
    this.allProjects = this.accountService.GetCurrentUserProjects();
    if (this.hasSelectedProject) {
      this.activeProjectTitle = this.accountService.GetActiveProject().projectTitle;
      this.currentColor = this.getProjectCss();
    }

    // 1 sec to ensure all the effort to get the css animation working is appreciated :|, Preboot screen is removed .5 sec later
    setTimeout(() => this.isAppLoaded = true, 50);
    setTimeout(() => this.removePrebootScreen = true, 50);

    setTimeout(() => {
      if (this.isUserLoggedIn) {
        this.alertService.resetStickyMessage();
      }
      else {
        this.router.navigateByUrl('/account/login');
      }
    }, 50);

    this.handleInactivityTimeout();
    this.handleRefreshToken();

    this.alertService.getDialogEvent().subscribe(alert => this.showDialog(alert));
    this.alertService.getMessageEvent().subscribe(message => this.showToast(message, false));
    this.alertService.getStickyMessageEvent().subscribe(message => this.showToast(message, true));

    this.authService.reLoginDelegate = () => this.authService.redirectLoginUser();

    this.authService.getLoginStatusEvent().subscribe(isLoggedIn => {
      this.isUserLoggedIn = isLoggedIn;


      if (this.isUserLoggedIn) {
        this.initNotificationsLoading();
        //console.log("Access token on login status change in app component:", this.authService.accessToken);
        this.hubService.removeOldConnection().then(() => {
          this.hubService.startConnection();
        });
        
      }
      else {
        this.hasSelectedProject = false;
        this.hasSelectedRole = false;
        this.unsubscribeNotifications();
        this.hubService.leavePresence();
      }

      setTimeout(() => {
        if (!this.isUserLoggedIn) {
          this.alertService.showMessage("Session Ended!", "", MessageSeverity.default);
        }
      }, 500);
    });

    this.authService.getActiveRoleChangeEvent().subscribe(activeRole => {
      if (activeRole) {
        this.hasSelectedRole = true;
      }
      else {
        this.hasSelectedRole = false;
      }
    });

    this.authService.getActiveProjectChangeEvent().subscribe(activeProject => {
      if (activeProject) {
        this.hasSelectedProject = true;
      }
      else {
        this.hasSelectedProject = false;
      }
    });

    this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        let url = (<NavigationStart>event).url;

        // Refresh projects list
        this.allProjects = this.accountService.GetCurrentUserProjects();
        if (url !== url.toLowerCase()) {
          this.router.navigateByUrl((<NavigationStart>event).url.toLowerCase());
        }
        console.log("NavigationStartEvent URL", url);
        if (this.authService.updatingTokenInProgress === false && this.authService.currentUser && !url.toLowerCase().includes(this.authService.loginUrl.toLowerCase())) {
          this.authService.resetTimeout();
          console.log("Reset timer manually on action");
        }
      }
    });

    this.authService.getActiveProjectChangeEvent().subscribe({
      next: (data) => {
        if (this.isUserLoggedIn){
          if (data){
            this.activeProjectTitle = data.projectTitle;
            this.currentColor = this.getProjectCss();
          } else {
            this.activeProjectTitle = null;
            this.currentColor = null;
          }
          
        }
      }, error: (error) => {
        console.log(error)
      }
    });
  }

  getProjectCss(){
    // Find the index of the current project title
    let index = this.allProjects?.map(p => p.projectTitle).indexOf(this.activeProjectTitle);
    return this.projectColorOptions[index % this.projectColorOptions.length];
  }

  ngOnDestroy() {
    this.unsubscribeNotifications();
  }

  private unsubscribeNotifications() {
    if (this.notificationsLoadingSubscription)
      this.notificationsLoadingSubscription.unsubscribe();
  }

  handleInactivityTimeout() {
    let confirmDialog: MatDialogRef<IdleDialogComponent>;

    // Subscribe to timer to handle inactivity messages alerts. This will not emit values until a user is logged in.
    // Takes a callback function to execute when the timer runs out.
    this.authService.getInactivityTimer(() => {
      console.log('LOGGING OUT');
      //this.authService.logout();
      this.authService.redirectLogoutUser();
      if (confirmDialog){
        this.warningIsBeingShown = false;
        confirmDialog.close();
      }
    })
      .subscribe(time => {
        //console.log("Inactivity timer subscription tick", (new Date().toISOString()), "time until inactivity logout (s):", time);
      if (!this.authService.isLoggedIn) { return; }

        if (this.authService.isSessionExpired) {
          this.alertService.showStickyMessage("Idle time limit passed.", `Automatically logged out.`, MessageSeverity.warn);
          this.authService.redirectLogoutUser();
          if (confirmDialog){
            this.warningIsBeingShown = false;
            confirmDialog.close();
          }
      }

      if (this.authService.shouldWarningShow && this.warningIsBeingShown == false) {
        const timeToWarnElapsed = this.authService.getTimeBeforeIdleWarning();
        // Prompt user for inactivity. If the dialog is dismissed, reset the timer.
        this.warningIsBeingShown = true;
        console.log("Prompting user for activity");

        confirmDialog = this.getConfirmDialog(
          'Inactivity Warning',
          `You were inactive for at least ${timeToWarnElapsed} minutes. Click "Stay Logged In" to continue working.`,
          'Stay Logged In'
        )

        confirmDialog.afterClosed().subscribe(_ => {
          console.log("Dismissal of dialog");
          if (!this.authService.isSessionExpired) {
            console.log("Resetting timeout token after dismissing refresh");
            this.authService.resetTimeout();
          }
          this.warningIsBeingShown = false;
        });
      }
    });
  }

  private readonly TOKEN_SAFETY_MARGIN_SECONDS = 60;

  handleRefreshToken() {
    // Timer callback branch
    console.log("Subscribing to auth token refresh timer");
    this.authService.getTokenExpiryTimer(() => {      
      if (this.authService.updatingTokenInProgress || !this.authService.isLoggedIn) { return; }

      console.log("Refreshing login auth token when from timer callback", (new Date().toISOString()));
      const beforeRefreshRole: Roles = this.accountService.GetActiveRole();
      const beforeRefreshProj = this.accountService.GetActiveProject();
      const beforeRefreshTeam = this.accountService.GetActiveTeam();

      this.authService.refreshLoginOnce().subscribe(_ => {
        console.log("Refreshed login auth token when timer ran out (timer callback final run)", (new Date().toISOString()));
        this.authService.isRestoringUserContextAfterRefresh = true;
        this.accountService.SetActiveRole(beforeRefreshRole);
        this.accountService.SetActiveProject(beforeRefreshProj);
        this.accountService.SetActiveTeam(beforeRefreshTeam);
        this.authService.setUserActiveValsSelected(true);
        setTimeout(() => {
          this.authService.isRestoringUserContextAfterRefresh = false;
        });
      });
    })
      .subscribe(time => {
        // Subscription branch, hit every second
        //console.log("Token expiry timer subscription tick", (new Date().toISOString()), "time until expiry (s):", time);
        if (!this.authService.isLoggedIn) { return; }

        if ((time <= this.TOKEN_SAFETY_MARGIN_SECONDS || this.authService.isTokenExpired) && this.authService.updatingTokenInProgress == false) {
          console.log("Refreshing login auth token when from subscription", (new Date().toISOString()));
          const beforeRefreshRole: Roles = this.accountService.GetActiveRole();
          const beforeRefreshProj = this.accountService.GetActiveProject();
          const beforeRefreshTeam = this.accountService.GetActiveTeam();

          this.authService.refreshLoginOnce().subscribe(_ => {
            console.log("Refreshed login auth token after expiration (subscription - this will generally run since we are subtracting time from the backend expiration time)", (new Date().toISOString()));
            this.authService.isRestoringUserContextAfterRefresh = true;
            this.accountService.SetActiveRole(beforeRefreshRole);
            this.accountService.SetActiveProject(beforeRefreshProj);
            this.accountService.SetActiveTeam(beforeRefreshTeam);
            this.authService.setUserActiveValsSelected(true);
            setTimeout(() => {
              this.authService.isRestoringUserContextAfterRefresh = false;
            });
          });
        }
      });
  }

  initNotificationsLoading() {

    this.notificationsLoadingSubscription = this.notificationService.getNewNotificationsPeriodically()
      .subscribe({
        next: notifications => {
        this.dataLoadingConsecutiveFailures = 0;
        this.newNotificationCount = notifications.filter(n => !n.isRead).length;
      },
        error: (error) => {
          this.alertService.logError(error);

          if (this.dataLoadingConsecutiveFailures++ < 20)
            setTimeout(() => this.initNotificationsLoading(), 5000);
          else
            this.alertService.showStickyMessage("Load Error", "Loading new notifications from the server failed!", MessageSeverity.error);
        }});
  }


  markNotificationsAsRead() {

    let recentNotifications = this.notificationService.recentNotifications;

    if (recentNotifications.length) {
      this.notificationService.readUnreadNotification(recentNotifications.map(n => n.id), true)
        .subscribe({
          next: response => {
          for (let n of recentNotifications) {
            n.isRead = true;
          }

          this.newNotificationCount = recentNotifications.filter(n => !n.isRead).length;
        },
          error: (error) => {
            this.alertService.logError(error);
            this.alertService.showMessage("Notification Error", "Marking read notifications failed", MessageSeverity.error);

          }});
    }
  }



  showDialog(dialog: AlertDialog) {

    alertify.set({
      labels: {
        ok: dialog.okLabel || "OK",
        cancel: dialog.cancelLabel || "Cancel"
      }
    });

    switch (dialog.type) {
      case DialogType.alert:
        alertify.alert(dialog.message);

        break
      case DialogType.confirm:
        alertify
          .confirm(dialog.message, (e) => {
            if (e) {
              dialog.okCallback();
            }
            else {
              if (dialog.cancelCallback)
                dialog.cancelCallback();
            }
          });

        break;
      case DialogType.prompt:
        alertify
          .prompt(dialog.message, (e, val) => {
            if (e) {
              dialog.okCallback(val);
            }
            else {
              if (dialog.cancelCallback)
                dialog.cancelCallback();
            }
          }, dialog.defaultValue);

        break;
    }
  }

  getConfirmDialog(dialogTitle: string, dialogText: string, buttonText: string): MatDialogRef<IdleDialogComponent>{
    return this.dialog.open(IdleDialogComponent, {
      data: {
        dialogTitle: dialogTitle,
        dialogText: dialogText,
        buttonText: buttonText
      },
      disableClose: true,
      closeOnNavigation: true
    })
  }

  showToast(message: AlertMessage | null, isSticky: boolean) {
    
    if (message == null) {
      // Clear all sticky toasts
      this.stickyToasties.forEach((toastId) => {
        this.toastrService.clear(toastId); // Clear each sticky toast
      });
      return;
    }

    let toastOptions = {
      title: message.summary,
      message: message.detail,
      timeOut: (isSticky || message.severity == 3) ? 0 : 4000, // No timeout if sticky
      closeButton: true,
      progressBar: true,
      extendedTimeOut: (isSticky || message.severity == 3) ? 0 : 4000
    };

    // If the message is sticky, we track the toastId
    if (isSticky) {
      let toast : any;
      switch (message.severity) {
        case MessageSeverity.info:
          toast = this.toastrService.info(message.summary, message.detail, toastOptions);
          break;
        case MessageSeverity.success:
          toast = this.toastrService.success(message.summary, message.detail, toastOptions);
          break;
        case MessageSeverity.error:
          toast = this.toastrService.error(message.summary, message.detail, toastOptions);
          break;
        case MessageSeverity.warn:
          toast = this.toastrService.warning(message.summary, message.detail, toastOptions);
          break;
        default:
          toast = this.toastrService.info(message.summary, message.detail, toastOptions);
          break;
      }
      toast.onShown.subscribe(() => {
        this.stickyToasties.push(toast.toastId); // Push the toast's id into the array to track it
      });
    } else {
      // For non-sticky messages, just show the toast
      switch (message.severity) {
        case MessageSeverity.info:
          this.toastrService.info(message.summary, message.detail, toastOptions);
          break;
        case MessageSeverity.success:
          this.toastrService.success(message.summary, message.detail, toastOptions);
          break;
        case MessageSeverity.error:
          this.toastrService.error(message.summary, message.detail, toastOptions);
          break;
        case MessageSeverity.warn:
          this.toastrService.warning(message.summary, message.detail, toastOptions);
          break;
        default:
          this.toastrService.info(message.summary, message.detail, toastOptions);
          break;
      }
    }
  }

  getMessageSeverityClass(message: AlertMessage){
    switch (message.severity){
      case MessageSeverity.info: return 'toast-info';
      break;
      case MessageSeverity.success: return 'toast-success';
      break;
      case MessageSeverity.error: return 'toast-error';
      break;
      case MessageSeverity.warn: return 'toast-warn'
      break;
      default: return 'toast-default';
    }
  }



  get userName(): string {
    return this.authService.currentUser ? this.authService.currentUser.userName : "";
  }

  get fullName(): string {
    return this.authService.currentUser ? this.authService.currentUser.fullName : "";
  }

  get role(): string {
    return this.authService.currentUser ?
      (
        this.authService.currentUser.activeRole != null ?
          (
            RolesNew[this.authService.currentUser.activeRole]
            //Roles[this.authService.currentUser.activeRole].charAt(0) == "Q" ?
            //  (Roles[this.authService.currentUser.activeRole].charAt(0).toUpperCase() + Roles[this.authService.currentUser.activeRole].charAt(1).toUpperCase() + Roles[this.authService.currentUser.activeRole].slice(2)) :
            //  (Roles[this.authService.currentUser.activeRole].charAt(0).toUpperCase() + Roles[this.authService.currentUser.activeRole].slice(1))
        ): ""
      ): "";
  }

  get project(): string {
    return this.authService.currentUser ?
      (
        this.authService.currentUser.activeProject != null ?
          this.authService.currentUser.activeProject.projectTitle : ""
      ) : "";
  }

  get canViewCustomers() {
    return this.accountService.userHasPermission(Permission.viewUsersPermission); //eg. viewCustomersPermission
  }

  get canViewProducts() {
    return this.accountService.userHasPermission(Permission.viewUsersPermission); //eg. viewProductsPermission
  }

  get canViewOrders() {
    return true; //eg. viewOrdersPermission
  }


}
